/**
 * チェックリスト表示の純粋ロジック
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business チェックリスト項目のカテゴリ別グルーピングとフィルタリングの純粋ロジック。
 * @graph-connects none
 */
import type {
  ChecklistCategory,
  ChecklistItem,
  DeviationLevel,
  PrincipleCode,
} from '@cortex/domain-creative/web';

/** チェックリストのフィルタ条件。 */
export interface ChecklistFilter {
  categoryId?: string;
  principleCode?: PrincipleCode;
  deviationLevel?: DeviationLevel;
  includeInactive?: boolean;
}

/**
 * チェックリスト項目をフィルタする。既定では非アクティブ (論理削除済み) を除外する。
 * @param items 全チェックリスト項目
 * @param filter フィルタ条件
 * @returns 条件に一致する項目 (番号昇順を保持)
 * @graph-connects none
 */
export function filterChecklistItems(
  items: readonly ChecklistItem[],
  filter: ChecklistFilter,
): ChecklistItem[] {
  return items.filter((item) => {
    if (!(filter.includeInactive ?? false) && !item.isActive) {
      return false;
    }
    if (filter.categoryId !== undefined && item.categoryId !== filter.categoryId) {
      return false;
    }
    if (filter.principleCode !== undefined && !item.principleCodes.includes(filter.principleCode)) {
      return false;
    }
    if (filter.deviationLevel !== undefined && item.deviationLevel !== filter.deviationLevel) {
      return false;
    }
    return true;
  });
}

/** カテゴリとその項目のペア。 */
export interface ChecklistGroup {
  category: ChecklistCategory;
  items: ChecklistItem[];
}

/**
 * チェックリスト項目をカテゴリ別にグルーピングする。項目のないカテゴリは除外する。
 * @param categories カテゴリ一覧 (order 昇順)
 * @param items 表示対象の項目
 * @returns カテゴリ順のグループ
 * @graph-connects none
 */
export function groupChecklistItems(
  categories: readonly ChecklistCategory[],
  items: readonly ChecklistItem[],
): ChecklistGroup[] {
  return categories
    .map((category) => ({
      category,
      items: items.filter((item) => item.categoryId === category.id),
    }))
    .filter((group) => group.items.length > 0);
}
